import ApiService from '@/services/ApiService';
import store from '@/store/store';
import ensureAdminIncluded from '@/components/NetworksAndAssociations/NetworkForm/NetworkInformationSection/utilities/usersAllowed';

class NetworksService {
  static async searchCompanies({
    search,
    page = 1,
    pageSize = 20,
    excludeMe = true,
  }) {
    const searchParams = new URLSearchParams();

    if (search) {
      searchParams.append('q', search);
    }

    searchParams.append('exclude_me', String(excludeMe));
    searchParams.append('page', String(page));
    searchParams.append('page_size', String(pageSize));

    const queryString = searchParams.toString();
    const url = `/api3/company/search?${queryString}`;
    const response = await ApiService.get(url);

    return response;
  }

  static async createNetwork({ network, invitations, logoFile }) {
    const url = '/api3/network/create/';

    const networkData = {
      name: network.name || '',
      description: network.description || '',
      network_commission: network.network_commission || '50%',
      has_group_chat: Boolean(network.has_group_chat),
      use_fake_address: network.address_type === 'publishing',
      daily_searches_enabled: Boolean(network.daily_searches_enabled),
      share_in_web: Boolean(network.share_in_web),
      share_in_web_for_owner: Boolean(network.share_in_web_for_owner),
      duplicate_leads_for_members: Boolean(network.duplicate_leads_for_members),
      ...(network.daily_searches_enabled && {
        operation_types_for_daily_search_ids:
          network.operation_types_for_daily_search_ids ?? [1, 2, 3],
      }),
    };

    if (network.type === 'association') {
      networkData.is_association = true;
      if (network.has_group_chat) {
        networkData.everyone_can_send_messages =
          network.everyone_can_send_messages !== undefined
            ? network.everyone_can_send_messages
            : true;
        if (networkData.everyone_can_send_messages === false) {
          const currentUserId = store.getState()?.UserSlice?.data?.id;
          networkData.users_allowed_ids = ensureAdminIncluded(
            network.users_allowed_ids,
            currentUserId
          );
        }
      }
    }

    if (logoFile) {
      const formData = new FormData();
      formData.append('logo', logoFile);
      formData.append('network', JSON.stringify(networkData));

      if (invitations && Array.isArray(invitations) && invitations.length > 0) {
        formData.append('invitations', JSON.stringify(invitations));
      }

      return ApiService.post(url, formData, undefined, true);
    }

    return ApiService.post(url, {
      network: networkData,
      invitations,
    });
  }

  static async getNetworksByStatus() {
    const url = '/api3/network/by_status';
    const response = await ApiService.get(url);

    return response;
  }

  static async getNetworkSummary() {
    const url = '/api3/network/summary/';
    const response = await ApiService.get(url);

    return response;
  }

  static async getNetwork(networkId) {
    const url = `/api3/network/${networkId}/`;
    const response = await ApiService.get(url);

    return response;
  }

  static async updateNetwork(
    networkId,
    { network, logoFile, logoRemoved, isAssociation }
  ) {
    const url = `/api3/network/${networkId}/`;

    const networkData = { ...network };

    if (isAssociation) {
      networkData.is_association = true;
      if (network.has_group_chat) {
        networkData.everyone_can_send_messages =
          network.everyone_can_send_messages !== undefined
            ? network.everyone_can_send_messages
            : true;
        if (networkData.everyone_can_send_messages === false) {
          const currentUserId = store.getState()?.UserSlice?.data?.id;
          networkData.users_allowed_ids = ensureAdminIncluded(
            network.users_allowed_ids,
            currentUserId
          );
        }
      }
    }

    if (logoFile) {
      const formData = new FormData();
      formData.append('logo', logoFile);
      formData.append('network', JSON.stringify(networkData));
      return ApiService.put(url, formData, undefined, true);
    }

    if (logoRemoved) {
      return ApiService.put(url, { ...networkData, logo: null });
    }

    return ApiService.put(url, networkData);
  }

  static async addInvitation({ invited_company, network }) {
    const url = '/api3/network/invitations/';
    const response = await ApiService.post(url, { invited_company, network });

    return response;
  }

  static async deleteInvitation(invitationId) {
    const url = `/api3/network/invitations/${invitationId}/`;
    const response = await ApiService.delete(url);

    return response;
  }

  static async rejectInvitation(invitationId, networkId) {
    const url = `/api3/network/invitations/${invitationId}/`;
    const response = await ApiService.put(url, {
      status: 'rejected',
      network: networkId,
    });

    return response;
  }

  static async acceptInvitation(invitationId, networkId) {
    const url = `/api3/network/invitations/${invitationId}/`;
    const response = await ApiService.put(url, {
      status: 'accepted',
      network: networkId,
    });

    return response;
  }

  static async deleteNetwork(networkId) {
    const url = `/api3/network/${networkId}/`;
    const response = await ApiService.delete(url);

    return response;
  }

  static async searchAgents({ q, networkId }) {
    const searchParams = new URLSearchParams();
    if (q) {
      searchParams.append('search', q);
    }

    const queryString = searchParams.toString();
    const queryParam = queryString ? `?${queryString}` : '';

    let url;
    if (networkId) {
      url = `/api3/network/chat-users/${networkId}/${queryParam}`;
    } else {
      url = `/api3/network/chat-users/${queryParam}`;
    }

    const response = await ApiService.get(url);

    const mappedResults = (response || []).map((chatUser) => ({
      id: chatUser.id,
      full_name: chatUser.full_name || chatUser.name || chatUser.username || '',
      profile_image: chatUser.photo,
      company_name: chatUser.company_name,
    }));

    return { users: mappedResults };
  }

  static async requestNetworksUpgrade() {
    const url = '/api3/company/upgrade_pending';
    const requestBody = { private_networks: true };
    const response = await ApiService.put(url, requestBody);
    return response;
  }
}

export default NetworksService;
